<template>
  <v-card
    tile
    flat
    outlined
    class="AutomationGridItem"
    @click="handleWorkflowClick"
  >
    <v-card-text class="AutomationGridItem__body">
      <v-row justify="space-between">
        <v-col align-self="center" cols="9">
          <p
            class="ma-0 text-subtitle-1 font-weight-bold AutomationGridItem__name"
          >
            {{ workflow.name }}
          </p>
        </v-col>
        <v-col align-self="center" cols="2">
          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-on="on" v-bind="attrs">
                <v-icon small> mdi-dots-vertical </v-icon>
              </v-btn>
            </template>

            <v-list>
              <v-list-item
                v-for="(item, index) in menuItems"
                :key="index"
                @click="item.action(workflow)"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    <v-icon small class="pr-2">
                      {{ item.icon }}
                    </v-icon>
                    {{ item.title }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-col>
      </v-row>

      <p class="my-6 text-body-2">{{ description }}</p>

      <v-row>
        <v-col>
          <span v-for="(connector, index) in connectors" :key="index">
            <ActionIcon :action="connector" />
          </span>
        </v-col>
      </v-row>

      <div class="AutomationGridItem__footer mt-auto">
        <v-row>
          <v-col cols="auto" align-self="center" class="mr-auto">
            <v-switch
              @click.native.stop
              v-model="workflowStatus"
              :loading="editLoading"
            ></v-switch>
          </v-col>

          <v-col cols="auto" align-self="end">
            <span>
              <strong>Next Run:</strong>
              {{ cronTime || 'Dynamic' }}
            </span>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="auto" align-self="center" class="mr-auto">
            <v-chip
              small
              :color="statusColor"
              class="AutomationGridItem__status"
            >
              {{ status }}
            </v-chip>
          </v-col>

          <v-col cols="auto" align-self="end">
            <workflow-owners :workflow="workflow" />
          </v-col>
        </v-row>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import * as cronParser from 'cron-parser';
import moment from 'moment';
import WorkflowOwners from '@/components/workflow-owners/workflow-owners.vue';
import extractConnectors from '@/util/get-connectors.js';
import ActionIcon from '@/components/action-icon/action-icon.vue';

const { mapGetters: customerGetters } = createNamespacedHelpers('customer');

export default {
  name: 'AutomationGridItem',
  components: {
    WorkflowOwners,
    ActionIcon,
  },
  props: {
    workflow: {
      type: Object,
      required: true,
    },
    editWorkflowLoading: {},
  },
  computed: {
    ...customerGetters({ customer: 'CUSTOMER' }),
    connectors() {
      if (this.workflow.connectors) {
        return extractConnectors(this.workflow.connectors);
      }

      return [];
    },
    workflowStatus: {
      get() {
        return Boolean(this.workflow.enabled);
      },
      set(value) {
        this.$emit('update-workflow', { id: this.workflow.id, enabled: value });
      },
    },
    cronTime() {
      if (this.workflow.trigger_type !== 'cron' || !this.workflow.trigger) {
        return '';
      }

      const interval = cronParser.parseExpression(this.workflow.trigger);
      const nextRun = moment(interval.next().toDate()).format(
        'YYYY-MM-DD HH:mm:ss'
      );
      return nextRun;
    },
    status() {
      return this.workflow.enabled ? 'enabled' : 'disabled';
    },
    statusColor() {
      return this.workflow.enabled ? 'secondary' : '';
    },
    description() {
      if (!this.workflow.description) {
        return '--';
      }

      return this.workflow.description.length > 170
        ? `${this.workflow.description.substr(0, 170)}...`
        : this.workflow.description;
    },
    editLoading() {
      return this.editWorkflowLoading(this.workflow.id);
    },
    sh_admin() {
      return this.customer.customer_id === 0;
    },
    isArchived() {
      return this.workflow.dateArchived;
    },
    menuItems() {
      const options = [
        {
          title: 'Show history',
          action: this.showWorkflowHistory,
          icon: 'mdi-history',
        },
        {
          title: 'Copy Workflow',
          action: this.copyWorkflow,
          icon: 'mdi-content-copy',
        },
        {
          title: `${!this.isArchived ? 'Archive' : 'Unarchive'} Workflow`,
          action: this.toggleArchiveWorkflow,
          icon: 'mdi-archive',
        },
        {
          title: 'Delete',
          action: this.deleteWorkflow,
          icon: 'mdi-delete',
        },
      ];
      const exportOptions = {
        title: 'Export',
        action: this.exportWorkflow,
        icon: 'mdi-export',
      };
      const exportAIOptions = {
        title: 'Export for AI',
        action: this.exportWorkflowAI,
        icon: 'mdi-robot',
      };
      if (this.sh_admin) {
        options.splice(1, 0, exportOptions, exportAIOptions);
      }
      return options;
    },
  },
  methods: {
    copyWorkflow(workflow) {
      this.$emit('copy-workflow', workflow);
    },
    toggleArchiveWorkflow(workflow) {
      const data = { id: workflow.id, dateArchived: null };
      if (!this.isArchived) {
        data.dateArchived = new Date();
      }
      this.$emit('update-workflow', data);
    },
    deleteWorkflow(workflow) {
      this.$emit('delete-workflow', workflow);
    },
    showWorkflowHistory(workflow) {
      this.$emit('show-history', workflow);
    },
    handleWorkflowClick() {
      this.$emit('go-to-workflow', this.workflow);
    },
    exportWorkflow(workflow) {
      this.$emit('export-workflow', workflow.id);
    },
    exportWorkflowAI(workflow) {
      this.$emit('export-workflow-ai', workflow.id);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './automation-grid-item.scss';
</style>
